import { useState } from "react";
import { Network, Plus, Trash2, Copy, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useSyslogSources,
  useCreateSyslogSource,
  useDeleteSyslogSource,
} from "@/hooks/use-api";
import { formatRelativeTime } from "@/lib/formatters";
import {
  RELAY_PORT,
  SYSLOG_VENDOR_LABELS,
  relayConfig,
} from "@/lib/syslog-relay";
import type { SyslogSourceCreated, SyslogVendor } from "@/types/api";

const VENDORS = Object.keys(SYSLOG_VENDOR_LABELS) as SyslogVendor[];

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      aria-label={label}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

export function SyslogSources() {
  const { data, isLoading, isError } = useSyslogSources();
  const createSource = useCreateSyslogSource();
  const deleteSource = useDeleteSyslogSource();

  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [vendor, setVendor] = useState<SyslogVendor>("fortinet");
  const [sourceIps, setSourceIps] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  // Held only until dismissed — the raw key is never retrievable again.
  const [created, setCreated] = useState<SyslogSourceCreated | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const sources = data?.sources ?? [];

  function resetForm() {
    setName("");
    setVendor("fortinet");
    setSourceIps("");
    setFormError(null);
  }

  function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError("Give the source a name.");
      return;
    }
    const ips = sourceIps
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setFormError(null);
    createSource.mutate(
      { name: trimmed, vendor_type: vendor, ...(ips.length ? { source_ips: ips } : {}) },
      {
        onSuccess: (res) => {
          setAddOpen(false);
          resetForm();
          setCreated(res.source);
        },
        onError: (err) => setFormError(err.message),
      },
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-3">
          <Network className="h-5 w-5 text-primary" />
          <div>
            <CardTitle>Firewall &amp; network syslog</CardTitle>
            <p className="text-sm text-muted-foreground">
              One source per device — parsing follows the vendor you pick here
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add source
        </Button>
      </CardHeader>
      <CardContent>
        {deleteError && (
          <p className="mb-3 flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {deleteError}
          </p>
        )}
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 2 }, (_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : isError ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Syslog sources could not be loaded. Try again shortly.
          </p>
        ) : sources.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No firewalls connected yet. Add a source to get an ingestion URL and
            key for your relay.
          </p>
        ) : (
          <ul className="divide-y">
            {sources.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{s.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {SYSLOG_VENDOR_LABELS[s.vendor_type] ?? s.vendor_type}
                    {" · "}
                    {s.last_event_at
                      ? `last event ${formatRelativeTime(s.last_event_at)}`
                      : "no events yet"}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteTarget({ id: s.id, name: s.name });
                  }}
                  aria-label={`Delete ${s.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {/* Add */}
      <Dialog
        open={addOpen}
        onOpenChange={(o) => {
          setAddOpen(o);
          if (!o) resetForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a firewall source</DialogTitle>
            <DialogDescription>
              You'll get an ingestion URL and a key to put in your relay's
              config. The key is shown once.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label htmlFor="syslog-name" className="text-sm font-medium">
                Name
              </label>
              <Input
                id="syslog-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Head office FortiGate"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="syslog-vendor" className="text-sm font-medium">
                Vendor
              </label>
              <select
                id="syslog-vendor"
                value={vendor}
                onChange={(e) => setVendor(e.target.value as SyslogVendor)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {VENDORS.map((v) => (
                  <option key={v} value={v}>
                    {SYSLOG_VENDOR_LABELS[v]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="syslog-ips" className="text-sm font-medium">
                Allowed source IPs{" "}
                <span className="font-normal text-muted-foreground">
                  (optional)
                </span>
              </label>
              <Input
                id="syslog-ips"
                value={sourceIps}
                onChange={(e) => setSourceIps(e.target.value)}
                placeholder="203.0.113.10, 198.51.100.0/24"
              />
              <p className="text-xs text-muted-foreground">
                The relay's public IP, not the firewall's LAN address. Leave
                blank to accept from anywhere.
              </p>
            </div>
            {formError && (
              <p className="flex items-start gap-2 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {formError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={createSource.isPending}>
              {createSource.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Created — key reveal + relay config */}
      <Dialog
        open={created !== null}
        onOpenChange={(o) => !o && setCreated(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{created?.name} is ready</DialogTitle>
            <DialogDescription>
              Copy the key now — it is not shown again. Install Fluent Bit on a
              host that can receive syslog from the firewall, save this as
              relay.conf, and run{" "}
              <code className="font-mono text-xs">
                fluent-bit -c relay.conf
              </code>
              . Then point the firewall's syslog at that host on UDP{" "}
              {RELAY_PORT}.
            </DialogDescription>
          </DialogHeader>
          {created && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded-md bg-muted px-3 py-2 font-mono text-xs">
                  {created.raw_key}
                </code>
                <CopyButton text={created.raw_key} label="Copy API key" />
              </div>
              {created.ingestion_url ? (
                <>
                  <div className="flex items-start gap-2">
                    <pre className="max-h-72 flex-1 overflow-auto rounded-md bg-muted p-3 font-mono text-xs">
                      {relayConfig(created.ingestion_url, created.raw_key)}
                    </pre>
                    <CopyButton
                      text={relayConfig(created.ingestion_url, created.raw_key)}
                      label="Copy relay config"
                    />
                  </div>
                </>
              ) : (
                <p className="flex items-start gap-2 text-sm text-muted-foreground">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  No ingestion URL is configured for this environment — contact
                  support before configuring your relay.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCreated(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.name}?</DialogTitle>
            <DialogDescription>
              Its key is revoked immediately and the relay stops being able to
              send. Events already ingested are retained.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!deleteTarget) return;
                const { name } = deleteTarget;
                deleteSource.mutate(deleteTarget.id, {
                  // Closing on click alone would tell the user the key was
                  // revoked when a 403/409/500 left it live.
                  onSuccess: () => setDeleteTarget(null),
                  onError: (err) => {
                    setDeleteTarget(null);
                    setDeleteError(`${name} was not deleted: ${err.message}`);
                  },
                });
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
